import { PledgeButton } from '@almbrand/pledgebutton';
import { ThemeContext } from '@almbrand/themeselector';
import { useNextUrl } from 'hooks/useHandleNextUrl';
import { usePageParam } from 'hooks/usePageParam';
import { useContext, useEffect, useRef } from 'react';
import { useAppSelector } from 'store/hooks';
import styles from './PledgeButton.module.scss';

export interface PledgeButtonProps extends WizardComponentBase {
	initialText: string;
	confirmingText: string;
	confirmedText: string;
	earlyReleaseErrorText: string;
	holdDuration?: number;
	confirmDelay?: number;
	redirectDelay?: number;
	value?: boolean | 'confirmed' | null;
	hasError?: boolean;
	register: any;
}

const PledgeButtonField: React.FC<PledgeButtonProps> = ({
	fieldName = '',
	required,
	initialText,
	confirmingText,
	confirmedText,
	earlyReleaseErrorText,
	holdDuration,
	confirmDelay,
	redirectDelay = 0,
	value = null,
	validationErrorFormatText,
	hasError,
	register,
}) => {
	const theme = useContext(ThemeContext);
	const fieldWatch = useAppSelector((store) => store.claimForm[fieldName]);
	const { setPageParam, getPageParam } = usePageParam();

	const currentPage = getPageParam();
	const nextUrls = useAppSelector(
		(store) =>
			store.claimContent.wizardData?.pages?.find((p) => p.path?.replace('/', '') === currentPage)?.navigation
				?.nextUrls
	);
	const { getNextUrl } = useNextUrl(nextUrls);

	const hasNavigatedRef = useRef(false);
	const redirectTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

	register(fieldName, { required });

	const persistedValue = fieldWatch?.data?.value ?? value;
	const startsConfirmed = persistedValue === true || persistedValue === 'confirmed';

	const navigateNextPage = async () => {
		const url = await getNextUrl();
		setPageParam(url?.replace('/', '') ?? '');
	};

	const handleConfirmed = ({ redirectDelay: delay }: { redirectDelay: number }) => {
		if (hasNavigatedRef.current) return;
		hasNavigatedRef.current = true;

		// Give the confirmed state time to render before moving on.
		if (delay > 0) {
			redirectTimerRef.current = setTimeout(() => {
				redirectTimerRef.current = null;
				void navigateNextPage();
			}, delay);
		} else {
			void navigateNextPage();
		}
	};

	useEffect(() => {
		// Resume navigation when a previously confirmed pledge is restored.
		if (!startsConfirmed || hasNavigatedRef.current) return;
		hasNavigatedRef.current = true;
		void navigateNextPage();
	}, []);

	useEffect(() => {
		return () => {
			if (redirectTimerRef.current !== null) {
				clearTimeout(redirectTimerRef.current);
				redirectTimerRef.current = null;
			}
		};
	}, []);

	return (
		<div className={styles.PledgeButton}>
			<PledgeButton
				themeName={theme}
				initialText={initialText}
				confirmingText={confirmingText}
				confirmedText={confirmedText}
				earlyReleaseErrorText={earlyReleaseErrorText}
				holdDuration={holdDuration}
				confirmDelay={confirmDelay}
				redirectDelay={redirectDelay}
				value={startsConfirmed ? 'confirmed' : null}
				onConfirmed={handleConfirmed}
			/>
			{hasError && validationErrorFormatText && (
				<p className={styles.PledgeButton__errorMessage}>{validationErrorFormatText}</p>
			)}
		</div>
	);
};

export { PledgeButtonField as PledgeButton };
